import React, { useEffect, useRef, useState } from 'react';
import { Wand2, Loader2, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { chatsApi } from '../../lib/chatsApi';
import { chatsErrorText, classifyChatsError, chatLog, CHAT_ERROR_WORDS } from '../../lib/chatsView';

/*
 * r16p (design 6.9, 7.1): "Suggest replies". Shown when the server's info says AI suggestions are on (info.ai.enabled) and the slot's
 * reply mode (772) is 2 "manual + AI suggestions". The job is async: POST /threads/{id}/suggest -> {jobId}, then GET
 * /suggestions/{jobId} every 2 s for up to 90 s. 1-3 chips; a tap COPIES the text into the composer, where it can be edited - sending
 * stays a separate press, and nothing is ever sent from here. The budget line is the server's (usedToday / limit). Errors in words:
 * ai_not_configured "AI suggestions are not set up yet", ai_busy, ai_budget_exhausted, rate_limited, ai_declined, ai_bad_output.
 *
 * The first use per account shows the server's processor notice (O19: the conversation is sent to Anthropic to draft the
 * suggestions) and goes on only after "Continue". The acknowledgement is a per-viewer convenience in localStorage, keyed by the
 * account's numeric id (never an email, never any text).
 *
 * Wrong-thread guard: the caller mounts one instance per conversation (key = thread id); an answer that arrives after a switch
 * belongs to an unmounted instance and is dropped.
 */
/** Poll every 2 s for up to 90 s (design 7.1). A module object so a test can shorten both. */
export const SUGGEST_TIMING = { pollMs: 2000, maxMs: 90000 };

const ackKey = (userId) => `necro.chats.aiNotice.${Number.isFinite(Number(userId)) ? Number(userId) : 'anon'}`;
function readAck(userId) {
  try { return window.localStorage.getItem(ackKey(userId)) === '1'; } catch (e) { return false; }
}
function writeAck(userId) {
  try { window.localStorage.setItem(ackKey(userId), '1'); } catch (e) { /* private window: ask again next time */ }
}

export default function SuggestionChips({ threadId, ai, userId, onPick, disabled }) {
  const [state, setState] = useState('idle'); // idle | notice | asking | done | error
  const [chips, setChips] = useState([]);
  const [jobId, setJobId] = useState(null);
  const [budget, setBudget] = useState(() => ({ usedToday: ai && ai.usedToday, limit: ai && ai.limit }));
  const [error, setError] = useState(null);
  const alive = useRef(true);
  const timer = useRef(null);
  useEffect(() => () => { alive.current = false; if (timer.current) clearTimeout(timer.current); }, []);

  const notConfigured = !!ai && ai.configured === false;

  const poll = (job, started) => {
    timer.current = setTimeout(async () => {
      if (!alive.current) return;
      if (Date.now() - started > SUGGEST_TIMING.maxMs) {
        setState('error'); setError(CHAT_ERROR_WORDS.ai_timeout);
        chatLog('warn', 'suggest timeout', { thread: threadId, job });
        return;
      }
      try {
        const res = await chatsApi.suggestion(job);
        if (!alive.current) return;
        const d = (res && res.data) || {};
        const st = String(d.state || '').toUpperCase();
        if (d.ai && typeof d.ai === 'object') setBudget({ usedToday: d.ai.usedToday, limit: d.ai.limit });
        if (st === 'DONE') {
          const list = (Array.isArray(d.suggestions) ? d.suggestions : [])
            .map((x) => (x && typeof x.text === 'string' ? x.text : (typeof x === 'string' ? x : null)))
            .filter((t) => t && t.trim()).slice(0, 3);
          setChips(list);
          setState(list.length ? 'done' : 'error');
          if (!list.length) setError(CHAT_ERROR_WORDS.ai_bad_output);
          chatLog('info', 'suggest done', { thread: threadId, job, n: list.length });
        } else if (st === 'FAILED') {
          const code = typeof d.error === 'string' ? d.error : (typeof d.errorCode === 'string' ? d.errorCode : null);
          setState('error'); setError((code && CHAT_ERROR_WORDS[code]) || CHAT_ERROR_WORDS.ai_bad_output);
          chatLog('info', 'suggest failed', { thread: threadId, job, code });
        } else {
          poll(job, started);
        }
      } catch (e) {
        if (!alive.current) return;
        const c = classifyChatsError(e);
        setState('error'); setError(chatsErrorText(e, 'Suggestions could not be loaded'));
        chatLog('warn', 'suggest poll error', { thread: threadId, job, status: c.status, code: c.code });
      }
    }, SUGGEST_TIMING.pollMs);
  };

  const ask = async () => {
    setState('asking'); setError(null); setChips([]);
    try {
      const res = await chatsApi.suggest(threadId);
      if (!alive.current) return;
      const d = (res && res.data) || {};
      if (d.ai && typeof d.ai === 'object') setBudget({ usedToday: d.ai.usedToday, limit: d.ai.limit });
      const job = d.jobId || d.job_id;
      if (!job) { setState('error'); setError(CHAT_ERROR_WORDS.ai_bad_output); return; }
      setJobId(String(job));
      chatLog('info', 'suggest asked', { thread: threadId, job: String(job) });
      poll(String(job), Date.now());
    } catch (e) {
      if (!alive.current) return;
      const c = classifyChatsError(e);
      setState('error'); setError(chatsErrorText(e, 'Suggestions could not be started'));
      chatLog('info', 'suggest refused', { thread: threadId, status: c.status, code: c.code });
    }
  };

  const press = () => {
    if (ai && ai.processorNotice && !readAck(userId)) { setState('notice'); return; }
    ask();
  };

  const usedLine = Number.isFinite(Number(budget.usedToday)) && Number.isFinite(Number(budget.limit)) && budget.limit !== null
    ? `${budget.usedToday} of ${budget.limit} suggestions used today` : null;

  return (
    <div className="border-t border-border/70 px-3 pt-2.5" data-testid="suggest-box" data-state={state} data-job={jobId || ''}>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" className="tap h-9 rounded-full border-accent/40 text-accent hover:bg-accent/10 hover:text-accent"
          onClick={press} disabled={disabled || notConfigured || state === 'asking' || state === 'notice'} data-testid="suggest-btn">
          {state === 'asking' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Wand2 className="h-4 w-4" aria-hidden />}
          {state === 'asking' ? 'Drafting suggestions…' : 'Suggest replies'}
        </Button>
        {notConfigured && <span className="text-xs text-muted-foreground" data-testid="suggest-not-configured">{CHAT_ERROR_WORDS.ai_not_configured}</span>}
        {usedLine && !notConfigured && <span className="text-[11px] text-muted-foreground tnum" data-testid="suggest-budget">{usedLine}</span>}
      </div>
      {state === 'notice' && (
        <div className="mt-2 rounded-xl border border-accent/30 bg-accent/10 p-3 text-sm" role="dialog" aria-label="About AI suggestions" data-testid="suggest-notice">
          <p className="flex items-start gap-2 text-foreground"><Info className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden /><span>{ai.processorNotice}</span></p>
          <div className="mt-2 flex gap-2">
            <Button size="sm" className="tap h-9 rounded-full" onClick={() => { writeAck(userId); ask(); }} data-testid="suggest-notice-ok">Continue</Button>
            <Button size="sm" variant="ghost" className="tap h-9 rounded-full" onClick={() => setState('idle')} data-testid="suggest-notice-cancel">Not now</Button>
          </div>
        </div>
      )}
      {state === 'error' && error && <p className="mt-1.5 text-xs text-warning" role="status" data-testid="suggest-error">{error}</p>}
      {state === 'done' && chips.length > 0 && (
        <div className="mt-2 flex flex-col gap-1.5" data-testid="suggest-chips">
          <p className="text-[11px] text-muted-foreground">Tap one to put it in the reply box. You can edit it; nothing is sent until you press Send.</p>
          {chips.map((t, i) => (
            <button key={i} type="button" onClick={() => onPick(t, jobId)} data-testid={`suggest-chip-${i}`}
              className="tap w-full rounded-xl border border-accent/25 bg-accent/[0.08] px-3 py-2 text-left text-sm text-foreground transition-colors hover:border-accent/50 hover:bg-accent/15">
              <span className="line-clamp-3 whitespace-pre-wrap break-words">{t}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
